<template>
  <div class="home">
    <!-- 添加结构化数据组件 -->
    <StructuredData 
      pageType="home" 
      :title="$t('home.meta_title', { appName: $t('app.name') })" 
      :description="$t('home.meta_description')" 
    />
    <!-- Hero Section -->
    <section class="hero">
      <div class="hero-content">
        <h1 class="hero-title">{{ $t('home.hero_title', { appName: $t('app.name') }) }}</h1>
        <p class="hero-subtitle">{{ $t('home.hero_subtitle') }}</p>
        <div class="hero-actions">
          <router-link to="/analyze" class="btn btn-lg" @click="trackAnalyzeClick">{{ $t('home.analyze_button') }}</router-link>
          <router-link to="/register" class="btn btn-outline btn-lg" @click="trackSignupClick">{{ $t('home.create_account') }}</router-link>
        </div>
      </div>
      <div class="hero-image">
        <div class="image-container">
          <img src="https://images.unsplash.com/photo-1463554050456-f2ed7d3fec09?ixlib=rb-4.0.3&q=85&fm=jpg&crop=entropy&cs=srgb&w=1000" 
               alt="Healthy plants" class="main-image" />
          <div class="floating-card card-1">
            <span class="icon">🔍</span>
            <span class="text">{{ $t('home.instant_analysis') }}</span>
          </div>
          <div class="floating-card card-2">
            <span class="icon">💧</span>
            <span class="text">{{ $t('home.smart_recommendations') }}</span>
          </div>
          <div class="floating-card card-3">
            <span class="icon">📱</span>
            <span class="text">{{ $t('home.mobile_friendly') }}</span>
          </div>
        </div>
      </div>
    </section>

    <!-- Features Section -->
    <section class="features-section">
      <h2 class="section-title">{{ $t('home.section_how_it_works') }}</h2>
      <div class="features">
        <div class="feature-card">
          <div class="feature-icon">📸</div>
          <h3>{{ $t('home.feature_1_title') }}</h3>
          <p>{{ $t('home.feature_1_desc') }}</p>
        </div>
        <div class="feature-card">
          <div class="feature-icon">🧠</div>
          <h3>{{ $t('home.feature_2_title') }}</h3>
          <p>{{ $t('home.feature_2_desc') }}</p>
        </div>
        <div class="feature-card">
          <div class="feature-icon">📋</div>
          <h3>{{ $t('home.feature_3_title') }}</h3>
          <p>{{ $t('home.feature_3_desc') }}</p>
        </div>
      </div>
      <div class="action-center">
        <router-link to="/analyze" class="btn">{{ $t('home.try_it_now') }}</router-link>
      </div>
    </section>

    <!-- Benefits Section -->
    <section class="benefits-section">
      <div class="content-wrapper">
        <div class="benefits-content">
          <h2>{{ $t('home.why_use') }}</h2>
          <ul class="benefits-list">
            <li>
              <span class="check-icon">✓</span>
              <span>{{ $t('home.benefit_1') }}</span>
            </li>
            <li>
              <span class="check-icon">✓</span>
              <span>{{ $t('home.benefit_2') }}</span>
            </li>
            <li>
              <span class="check-icon">✓</span>
              <span>{{ $t('home.benefit_3') }}</span>
            </li>
            <li>
              <span class="check-icon">✓</span>
              <span>{{ $t('home.benefit_4') }}</span>
            </li>
            <li>
              <span class="check-icon">✓</span>
              <span>{{ $t('home.benefit_5') }}</span>
            </li>
          </ul>
        </div>
        <div class="benefits-image">
          <img src="https://images.unsplash.com/photo-1466692476868-aef1dfb1e735?ixlib=rb-4.0.3&q=85&fm=jpg&crop=entropy&cs=srgb&w=600" 
               alt="Healthy plant collection" />
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="cta-section">
      <div class="cta-content">
        <h2>{{ $t('home.ready_to_revive') }}</h2>
        <p>{{ $t('home.join_thousands') }}</p>
        <div class="cta-buttons">
          <router-link to="/analyze" class="btn" @click="trackAnalyzeClick">{{ $t('home.analyze_now') }}</router-link>
          <router-link to="/register" class="btn btn-outline" @click="trackSignupClick">{{ $t('home.sign_up_free') }}</router-link>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { onMounted } from 'vue';
import { useHead } from '@vueuse/head';
import { useI18n } from 'vue-i18n';
import { logger } from '@/utils/logger';
import StructuredData from '@/components/StructuredData.vue';

const { t } = useI18n();

// Set page meta tags
useHead({
  title: t('home.meta_title', { appName: t('app.name') }), // e.g., "WhisperHerb: AI Plant Care Assistant & Disease Diagnosis"
  meta: [
    { 
      name: 'description', 
      content: t('home.meta_description') // e.g., "Get expert plant care advice, diagnose diseases with a photo, and receive personalized recommendations with WhisperHerb, your AI-powered plant care companion."
    },
    // Add other relevant meta tags if needed (e.g., Open Graph)
    { property: 'og:title', content: t('home.meta_title', { appName: t('app.name') }) },
    { property: 'og:description', content: t('home.meta_description') },
    // { property: 'og:image', content: 'URL_to_your_featured_image.jpg' } 
  ],
});

// Track page view when component mounts
onMounted(() => {
  logger.trackEvent('home_view_opened');
});

// Track button clicks
const trackAnalyzeClick = () => {
  logger.trackEvent('home_analyze_clicked');
};

const trackSignupClick = () => {
  logger.trackEvent('home_signup_clicked');
};
</script>

<style scoped>
.home {
  overflow-x: hidden;
}

/* Hero Section */
.hero {
  display: flex;
  align-items: center;
  padding: var(--space-2xl) 0;
  min-height: 80vh;
  position: relative;
}

.hero-content {
  flex: 1;
  max-width: 600px;
  padding: 0 var(--space-xl);
  z-index: 2;
}

.hero-title {
  font-size: 3.5rem;
  line-height: 1.2;
  margin-bottom: var(--space-md);
  color: var(--text-color);
}

.highlight {
  color: var(--primary-color);
  position: relative;
  display: inline-block;
}

.highlight::after {
  content: '';
  position: absolute;
  left: 0;
  bottom: 5px;
  width: 100%;
  height: 12px;
  background-color: var(--primary-light);
  z-index: -1;
  transform: skew(-12deg);
}

.hero-subtitle {
  font-size: 1.5rem;
  color: var(--text-secondary);
  margin-bottom: var(--space-xl);
  line-height: 1.4;
}

.hero-actions {
  display: flex;
  gap: var(--space-md);
}

.hero-image {
  flex: 1;
  position: relative;
  height: 100%;
  max-width: 600px;
}

.image-container {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 400px;
}

.main-image {
  width: 100%;
  height: auto;
  max-height: 550px;
  object-fit: cover;
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
}

.floating-card {
  position: absolute;
  background-color: white;
  border-radius: var(--radius-lg);
  padding: var(--space-md);
  box-shadow: var(--shadow-md);
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  z-index: 2;
  animation: float 5s ease-in-out infinite;
}

.floating-card .icon {
  font-size: 1.5rem;
}

.floating-card .text {
  font-weight: 500;
  font-size: 0.9rem;
  color: var(--text-color);
}

.card-1 {
  top: 20%;
  left: -15%;
  animation-delay: 0s;
}

.card-2 {
  top: 50%;
  right: -10%;
  animation-delay: 1s;
}

.card-3 {
  bottom: 15%;
  left: 10%;
  animation-delay: 2s;
}

@keyframes float {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-15px);
  }
}

/* Features Section */
.features-section {
  padding: var(--space-2xl) var(--space-xl);
  background-color: var(--bg-color);
  text-align: center;
}

.section-title {
  font-size: 2.5rem;
  margin-bottom: var(--space-2xl);
  position: relative;
  display: inline-block;
}

.section-title::after {
  content: '';
  position: absolute;
  bottom: -10px;
  left: 50%;
  transform: translateX(-50%);
  width: 80px;
  height: 4px;
  background-color: var(--primary-color);
  border-radius: 10px;
}

.features {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: var(--space-xl);
  margin-bottom: var(--space-2xl);
}

.feature-card {
  background-color: var(--bg-light);
  border-radius: var(--radius-lg);
  padding: var(--space-xl);
  box-shadow: var(--shadow-md);
  max-width: 320px;
  transition: transform var(--transition-normal), box-shadow var(--transition-normal);
}

.feature-card:hover {
  transform: translateY(-10px);
  box-shadow: var(--shadow-lg);
}

.feature-icon {
  font-size: 3rem;
  margin-bottom: var(--space-md);
}

.feature-card h3 {
  margin-bottom: var(--space-md);
  color: var(--primary-dark);
}

.feature-card p {
  color: var(--text-secondary);
}

.action-center {
  margin-top: var(--space-xl);
}

/* Benefits Section */
.benefits-section {
  background-color: var(--primary-light);
  padding: var(--space-2xl) var(--space-xl);
}

.content-wrapper {
  display: flex;
  max-width: var(--container-lg);
  margin: 0 auto;
  gap: var(--space-2xl);
  align-items: center;
}

.benefits-content {
  flex: 1;
}

.benefits-content h2 {
  font-size: 2.5rem;
  margin-bottom: var(--space-xl);
  color: var(--primary-dark);
}

.benefits-list {
  list-style: none;
}

.benefits-list li {
  margin-bottom: var(--space-lg);
  display: flex;
  align-items: flex-start;
  gap: var(--space-md);
  font-size: 1.1rem;
}

.check-icon {
  color: var(--primary-color);
  font-weight: bold;
  font-size: 1.3rem;
}

.benefits-image {
  flex: 1;
  max-width: 500px;
}

.benefits-image img {
  width: 100%;
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
}

/* CTA Section */
.cta-section {
  background: var(--primary-gradient);
  padding: var(--space-2xl) var(--space-xl);
  text-align: center;
}
.cta-content {
  max-width: 800px;
  margin: 0 auto;
}
.cta-content h2 {
  font-size: 2.5rem;
  margin-bottom: var(--space-md);
  color: var(--primary-dark); /* Light green color for heading */
}
.cta-content p {
  font-size: 1.25rem;
  margin-bottom: var(--space-xl);
  color: var(--primary-dark); /* Slightly lighter green for paragraph */
}
.cta-buttons {
  display: flex;
  justify-content: center;
  gap: var(--space-md);
}
.cta-buttons .btn {
  background-color: white;
  color: var(--primary-dark);
}
.cta-buttons .btn:hover {
  background-color: rgba(255, 255, 255, 0.9);
}
.cta-buttons .btn-outline {
  background-color: transparent;
  border: 2px solid var(--primary-dark);
  color: var(--primary-dark);
}
.cta-buttons .btn-outline:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

/* Responsive Styles */
@media (max-width: 992px) {
  .hero {
    flex-direction: column;
    text-align: center;
    padding-bottom: 0;
  }
  
  .hero-content {
    max-width: 100%;
    padding: var(--space-xl) var(--space-md);
  }
  
  .hero-actions {
    justify-content: center;
  }
  
  .hero-image {
    max-width: 100%;
    margin-top: var(--space-xl);
  }
  
  .card-1 {
    left: 5%;
  }
  
  .card-2 {
    right: 5%;
  }
}

@media (max-width: 768px) {
  .hero-title {
    font-size: 2.5rem;
  }
  
  .hero-subtitle {
    font-size: 1.25rem;
  }
  
  .floating-card {
    display: none;
  }
  
  .features {
    gap: var(--space-lg);
  }
  
  .feature-card {
    max-width: 100%;
    width: 100%;
  }
  
  .content-wrapper {
    flex-direction: column;
  }
  
  .benefits-image {
    order: -1;
    margin-bottom: var(--space-xl);
  }
  
  .section-title,
  .benefits-content h2,
  .cta-content h2 {
    font-size: 2rem;
  }
}

@media (max-width: 480px) {
  .hero-actions,
  .cta-buttons {
    flex-direction: column;
  }
  
  .hero-title {
    font-size: 2rem;
  }
  
  .benefits-list li {
    font-size: 1rem;
  }
}
</style>
